import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Card, Input, Button, message } from 'antd';
import 'antd/dist/reset.css';

const { TextArea } = Input;

const ChatBoxComponent = () => {
  // Ở giao diện khách hàng, currentUser = conversationCustomer
  const currentUser = localStorage.getItem('name') || 'customer';
  const conversationCustomer = localStorage.getItem('conversationCustomer') || currentUser;

  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = useCallback(async () => {
    try {
      const res = await fetch(`/api/chat/conversation/${conversationCustomer}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setChatMessages(data);
      } else {
        console.error('Response is not an array:', data);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  }, [conversationCustomer]);

  // Khi gửi tin nhắn từ ChatBoxComponent thì type là "KH"
  const handleSendMessage = async () => {
    const contentToSend = newMessage.trim();
    if (!contentToSend) return;
    try {
      const res = await fetch(`/api/chat/conversation/${conversationCustomer}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToSend, sender: currentUser, type: 'KH' }),
      });
      if (!res.ok) throw new Error('Error sending message');
      const savedMsg = await res.json();
      setChatMessages(prev => [...prev, savedMsg]);
      setNewMessage('');
    } catch (error) {
      console.error(error);
      message.error('Error sending message');
    }
  };

  // Khi khách hàng xem hội thoại, gửi reader = currentUser
  const markConversationAsRead = useCallback(async () => {
    try {
      const res = await fetch(`/api/chat/conversation/${conversationCustomer}/mark_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reader: currentUser }),
      });
      if (!res.ok) throw new Error('Error marking messages as read');
      const data = await res.json();
      console.log(data.message);
      // Cập nhật lại local state cho các tin nhắn chưa được đánh dấu
      // Ở phiên bản này, chúng ta không giới hạn hiển thị read receipt chỉ cho tin nhận mà cho cả tin đã gửi
      setChatMessages(prevMessages =>
        prevMessages.map(msg => {
          // Nếu tin đã được cập nhật (read_time khác rỗng) thì giữ nguyên,
          // nếu chưa có read_time và tin không do chính khách gửi, ta có thể cập nhật tạm thời cho UI (chỉ hiển thị cục bộ)
          if (msg.read_time && msg.read_time !== '') {
            return msg;
          }
          // Ở giao diện khách, backend sẽ cập nhật các tin của đối phương (tin nhân viên, type NV)
          // Tuy nhiên, nếu có thông tin read receipt (đã được cập nhật từ phía server) thì hiển thị cho tất cả
          return msg;
        })
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [currentUser, conversationCustomer]);

  useEffect(() => {
    fetchConversation();
    markConversationAsRead();
    const interval = setInterval(() => {
      fetchConversation();
      markConversationAsRead();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchConversation, markConversationAsRead]);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Container fluid className="p-0" style={{ paddingTop: '80px', height: '100%' }}>
      <Row className="h-100 m-0">
        <Col xs={12} md={9} lg={12} className="p-3 d-flex flex-column">
          <Card
            title="Tin Nhắn"
            bordered={false}
            style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            bodyStyle={{ flexGrow: 1, display: 'flex', flexDirection: 'column', padding: '0' }}
          >
            <div
              style={{
                flexGrow: 1,
                overflowY: 'auto',
                padding: '1rem',
                background: '#f8f9fa',
                height: '75vh',
              }}
            >
              {chatMessages.map(msg => {
                const isMyMessage = msg.sender === currentUser;
                return (
                  <div
                    key={msg.id}
                    style={{
                      display: 'flex',
                      justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                      marginBottom: '1rem',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '100%',
                        padding: '0.75rem 1rem',
                        borderRadius: '15px',
                        backgroundColor: isMyMessage ? '#1890ff' : '#e2e2e2',
                        color: isMyMessage ? '#fff' : '#000',
                        wordWrap: 'break-word',
                      }}
                    >
                      <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        {msg.sender}
                      </div>
                      <div>{msg.content}</div>
                      <div
                        style={{
                          fontSize: '0.65rem',
                          color: isMyMessage ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.6)',
                          marginTop: '0.25rem',
                          textAlign: 'right',
                        }}
                      >
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </div>
                      {/* Hiển thị thông tin read receipt cho mọi tin nếu có */}
                      {msg.read_time && (
                        <div style={{ fontSize: '0.65rem', textAlign: 'right', marginTop: '0.25rem', color: '#FF3333' }}>
                          Đã đọc: {msg.read_time} {msg.read_user && `- Người đọc: ${msg.read_user}`}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ borderTop: '1px solid #ddd', padding: '0.75rem 1rem', background: '#fff' }}>
              <div style={{ display: 'flex' }}>
                <TextArea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  placeholder="Nhập tin nhắn..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onKeyDown={handleKeyDown}
                  style={{ flexGrow: 1 }}
                />
                <Button type="primary" style={{ marginLeft: '0.5rem' }} onClick={handleSendMessage}>
                  Gửi
                </Button>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ChatBoxComponent;
